// Spec 34. Source-of-truth glyph mapping for graph nodes.
//
// We normalize raw source names first (`github:owner/repo`, custom webhook
// names like `linear-prod`, etc.) so the UI doesn't depend on an exact string
// match. Graph nodes still draw a small brand-tinted disc; legend chips use
// real brand marks where we know them.

import {
	siGithub,
	siGmail,
	siHubspot,
	siIntercom,
	siJira,
	siLinear,
	siMixpanel,
	siPagerduty,
	siSentry,
	siStripe,
	siZendesk,
} from "simple-icons";

export interface SourceGlyph {
	label: string;
	bg: string;
	icon?: { path: string; hex: string; color?: string };
}

export const SOURCE_GLYPH: Record<string, SourceGlyph> = {
	github: {
		label: "GitHub",
		bg: "#24292f",
		// GitHub's brand mark is near-black by default, which disappears on our
		// dark glass chips. Use the approved light-on-dark treatment here.
		icon: { path: siGithub.path, hex: siGithub.hex, color: "#f8fafc" },
	},
	gmail: {
		label: "Gmail",
		bg: "#ffffff",
		icon: { path: siGmail.path, hex: siGmail.hex },
	},
	webhook: { label: "Webhook", bg: "#22d3ee" },
	linear: {
		label: "Linear",
		bg: "#111827",
		icon: { path: siLinear.path, hex: siLinear.hex, color: "#f8fafc" },
	},
	stripe: {
		label: "Stripe",
		bg: "#635bff",
		icon: { path: siStripe.path, hex: siStripe.hex },
	},
	posthog: {
		label: "PostHog",
		bg: "#000000",
	},
	amplitude: {
		label: "Amplitude",
		bg: "#1f47ff",
	},
	mixpanel: {
		label: "Mixpanel",
		bg: "#7856ff",
		icon: { path: siMixpanel.path, hex: siMixpanel.hex },
	},
	hubspot: {
		label: "HubSpot",
		bg: "#ff7a59",
		icon: { path: siHubspot.path, hex: siHubspot.hex },
	},
	salesforce: {
		label: "Salesforce",
		bg: "#00a1e0",
	},
	zendesk: {
		label: "Zendesk",
		bg: "#03363d",
		icon: { path: siZendesk.path, hex: siZendesk.hex },
	},
	intercom: {
		label: "Intercom",
		bg: "#1f8ded",
		icon: { path: siIntercom.path, hex: siIntercom.hex },
	},
	sentry: {
		label: "Sentry",
		bg: "#362d59",
		icon: { path: siSentry.path, hex: siSentry.hex },
	},
	pagerduty: {
		label: "PagerDuty",
		bg: "#06ac38",
		icon: { path: siPagerduty.path, hex: siPagerduty.hex },
	},
	jira: {
		label: "Jira",
		bg: "#0052cc",
		icon: { path: siJira.path, hex: siJira.hex },
	},
};

const NORMALIZED_SOURCE_ENTRIES = Object.keys(SOURCE_GLYPH).filter(
	(key) => key !== "webhook",
);

export function normalizeSourceGlyphKey(
	source: string | null | undefined,
): string | null {
	if (!source) return null;
	const raw = source.toLowerCase();
	if (raw === "manual" || raw === "communities") return null;
	if (raw.startsWith("github:")) return "github";
	if (raw.startsWith("gmail")) return "gmail";
	if (raw.startsWith("inbound") || raw.startsWith("webhook")) return "webhook";

	const normalized = raw.replace(/[^a-z0-9]+/g, "");
	for (const key of NORMALIZED_SOURCE_ENTRIES) {
		if (normalized.includes(key)) return key;
	}
	return null;
}

export function sourceGlyphFor(
	source: string | null | undefined,
): SourceGlyph | null {
	const key = normalizeSourceGlyphKey(source);
	return key ? SOURCE_GLYPH[key] ?? null : null;
}

// Draw a source badge centered at (cx, cy) with radius `r` onto a 2D
// canvas: a brand-coloured disc with the brand icon (when known) inset
// inside it. Both graph canvases call this from their overlay-redraw
// pass. The caller is responsible for anchoring the badge relative to a
// node and for picking `r` from the node's *on-screen* size (use
// sigma's `scaleSize`, not the raw attribute, so the badge tracks zoom).
export function drawSourceBadge(
	ctx: CanvasRenderingContext2D,
	cx: number,
	cy: number,
	r: number,
	glyph: SourceGlyph,
): void {
	ctx.save();
	// Disc.
	ctx.beginPath();
	ctx.arc(cx, cy, r, 0, Math.PI * 2);
	ctx.fillStyle = glyph.bg;
	ctx.fill();
	// Hairline ring so light discs (Gmail white) still read against a
	// light backdrop and dark discs separate from the node.
	ctx.lineWidth = 1;
	ctx.strokeStyle = "rgba(0,0,0,0.55)";
	ctx.stroke();
	// Brand icon, centered in the disc at ~70% of its width. simple-icons
	// paths are authored against a 24x24 viewBox.
	if (glyph.icon) {
		const path = new Path2D(glyph.icon.path);
		const scale = (r * 1.4) / 24;
		ctx.translate(cx - (24 * scale) / 2, cy - (24 * scale) / 2);
		ctx.scale(scale, scale);
		ctx.fillStyle = glyph.icon.color ?? `#${glyph.icon.hex}`;
		ctx.fill(path);
	}
	ctx.restore();
}

export function SourceBrandIcon({
	source,
	className = "w-3 h-3",
}: {
	source: string;
	className?: string;
}) {
	const glyph = sourceGlyphFor(source);
	if (!glyph) return null;
	if (!glyph.icon) {
		return (
			<span
				className={`${className} inline-block rounded-full`}
				style={{ background: glyph.bg }}
				aria-hidden="true"
			/>
		);
	}
	return (
		<svg
			viewBox="0 0 24 24"
			className={`${className} shrink-0`}
			aria-hidden="true"
			fill={glyph.icon.color ?? `#${glyph.icon.hex}`}
			style={{
				color: glyph.icon.color ?? `#${glyph.icon.hex}`,
			}}
		>
			<path d={glyph.icon.path} />
		</svg>
	);
}
